import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  useApi,
  SkillLevel,
  SKILL_LEVELS,
  Currency,
  SUPPORTED_CURRENCIES,
  CURRENCY_SYMBOLS,
  PlayType,
  PLAY_TYPES,
} from '../api';
import { useI18n } from '../i18n';
import { Icon, IconName } from '../Icon';
import {
  defaultWallStartAt,
  getAppTimeZone,
  setAppTimeZone,
  wallClockToUtcIso,
} from '../lib/datetime';
import { coverForPlayType } from '../lib/play-type';
import { readImageAsDataUrl } from '../lib/read-image-file';
import './CreateGame.css';

const SKILL_ICONS: Record<SkillLevel, IconName> = {
  LEVEL_1: 'tennis-ball',
  LEVEL_2: 'play',
  LEVEL_3: 'medal-01',
  LEVEL_4: 'award-01',
  LEVEL_5: 'star',
  LEVEL_6: 'crown',
};

type CoverChoice =
  | { kind: 'none' }
  | { kind: 'saved'; url: string }
  | { kind: 'fromGame'; gameId: string; url: string }
  | { kind: 'upload'; base64: string; mime: string; preview: string };

type LastHostedCover = {
  gameId: string;
  url: string;
  startAt: string;
} | null;

export function CreateGamePage() {
  const api = useApi();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useI18n();

  const meQ = useQuery(['me'], () => api.me());
  const cityQ = useQuery(['default-city'], () => api.defaultCity());
  const filterCity = meQ.data?.city || cityQ.data?.city || undefined;

  // Keep create-form wall clock in sync with bot / APP_TIMEZONE.
  useEffect(() => {
    if (cityQ.data?.timeZone) setAppTimeZone(cityQ.data.timeZone);
  }, [cityQ.data?.timeZone]);
  // City catalog + venues from games this user hosted (covers city-alias
  // mismatches and places used before the picker knew about them).
  // Also remembers the latest hosted cover so Create can reuse it without
  // a second round-trip when the profile library is still empty.
  const venuesQ = useQuery(
    ['venues', filterCity, meQ.data?.id, 'with-hosted'],
    async () => {
      const catalog = await api.listVenues({ city: filterCity });
      if (!meQ.data?.id) return { venues: catalog, lastCover: null as LastHostedCover };
      const from = new Date();
      from.setFullYear(from.getFullYear() - 2);
      const hosted = await api.listGames({
        hostId: meQ.data.id,
        from: from.toISOString(),
        to: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        includeClosed: true,
      });
      const byId = new Map(catalog.map((v) => [v.id, v]));
      let lastCover: LastHostedCover = null;
      for (const g of hosted) {
        const v = g.venue;
        if (v?.id && !byId.has(v.id)) {
          byId.set(v.id, {
            id: v.id,
            name: v.name,
            address: v.address,
            city: v.city ?? filterCity ?? '',
            lat: v.lat,
            lng: v.lng,
            indoor: v.indoor,
            surface: null,
            hourlyPrice: 0,
            capacity: g.spotsTotal,
          });
        }
        if (g.coverImageUrl?.trim()) {
          if (
            !lastCover ||
            new Date(g.startAt).getTime() > new Date(lastCover.startAt).getTime()
          ) {
            lastCover = {
              gameId: g.id,
              url: g.coverImageUrl,
              startAt: g.startAt,
            };
          }
        }
      }
      return {
        venues: [...byId.values()].sort((a, b) => a.name.localeCompare(b.name)),
        lastCover,
      };
    },
    { enabled: !!filterCity },
  );

  const venues = venuesQ.data?.venues;

  useEffect(() => {
    if (coverSeeded.current) return;
    if (meQ.data?.savedCoverImageUrl?.trim()) return;
    const last = venuesQ.data?.lastCover;
    if (!last) {
      if (venuesQ.isSuccess) coverSeeded.current = true;
      return;
    }
    coverSeeded.current = true;
    setCover({ kind: 'fromGame', gameId: last.gameId, url: last.url });
  }, [meQ.data?.savedCoverImageUrl, venuesQ.data?.lastCover, venuesQ.isSuccess]);

  const [venueId, setVenueId] = useState('');
  const [venueName, setVenueName] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [startAt, setStartAt] = useState(() => defaultWallStartAt());
  const [durationHours, setDurationHours] = useState<number | ''>(2);
  const [skill, setSkill] = useState<SkillLevel>('LEVEL_3');
  // 0 = unlimited. We use 0 because the field minimum is 1 in HTML, but we
  // show a toggle that flips to "unlimited" and clears the cap.
  const [unlimitedSpots, setUnlimitedSpots] = useState(false);
  const [spotsTotal, setSpotsTotal] = useState(10);
  const [currency, setCurrency] = useState<Currency>('PLN');
  // cost is stored as decimal in major units (e.g. 25.00)
  const [totalCostDecimal, setTotalCostDecimal] = useState<string>('');
  const [notes, setNotes] = useState('');
  // `addressHint` was an optional secondary field for describing a meeting
  // point next to the venue address. We dropped it — meeting specifics now
  // live in `notes` only — so no client-side state is needed here.
  const [isClosed, setIsClosed] = useState(false);
  const [hidePlayersFromOutsiders, setHidePlayersFromOutsiders] = useState(false);
  const [playType, setPlayType] = useState<PlayType>('OUTDOOR');
  const [cover, setCover] = useState<CoverChoice>({ kind: 'none' });
  const [coverError, setCoverError] = useState<string | null>(null);
  const coverRef = useRef<HTMLInputElement>(null);
  const coverSeeded = useRef(false);

  // Prefill from profile library as soon as /me is available.
  useEffect(() => {
    if (coverSeeded.current) return;
    const saved = meQ.data?.savedCoverImageUrl?.trim();
    if (!saved) return;
    coverSeeded.current = true;
    setCover({ kind: 'saved', url: saved });
  }, [meQ.data?.savedCoverImageUrl]);

  const selectedVenue = useMemo(
    () => venues?.find((v) => v.id === venueId),
    [venues, venueId],
  );

  const [costTouched, setCostTouched] = useState(false);
  const suggestedCostDecimal = selectedVenue
    ? ((selectedVenue.hourlyPrice * (durationHours === '' ? 1 : durationHours)) / 100).toFixed(2)
    : '0.00';
  const finalCostDecimal = costTouched ? totalCostDecimal : suggestedCostDecimal;
  // `isPaid` is no longer user-toggled. It is derived from the total cost:
  // a game is paid when the organizer enters a non-zero amount. Leaving the
  // cost at zero means the game is free.
  const isPaid = Number(finalCostDecimal) > 0;
  const perPlayer = unlimitedSpots
    ? (Number(finalCostDecimal) || 0).toFixed(2)
    : spotsTotal > 0
      ? ((Number(finalCostDecimal) || 0) / spotsTotal).toFixed(2)
      : '0.00';

  const endAtIso = useMemo(() => {
    const start = new Date(wallClockToUtcIso(startAt, getAppTimeZone()));
    const hours = durationHours === '' ? 1 : durationHours;
    const end = new Date(start.getTime() + hours * 3600_000);
    return end.toISOString();
  }, [startAt, durationHours]);

  // When user toggles "unlimited", snap to a sane max. We let the API accept
  // up to 1000 spots; the field is capped at the same.
  useEffect(() => {
    if (unlimitedSpots && spotsTotal < 100) setSpotsTotal(100);
  }, [unlimitedSpots]); // eslint-disable-line react-hooks/exhaustive-deps

  const createMut = useMutation(
    async () => {
      const g = await api.createGame({
        venueId: venueId || undefined,
        venueName: venueName.trim() || undefined,
        venueAddress: venueAddress.trim(),
        startAt: wallClockToUtcIso(startAt, getAppTimeZone()),
        endAt: endAtIso,
        skillLevel: skill,
        spotsTotal: unlimitedSpots ? Math.max(spotsTotal, 100) : spotsTotal,
        totalCost: Math.round((Number(finalCostDecimal) || 0) * 100),
        notes: notes || undefined,
        currency,
        isPaid,
        isClosed,
        hidePlayersFromOutsiders,
        coverImageUrl: undefined,
        playType,
      });
      try {
        if (cover.kind === 'upload') {
          await api.setGameCovers(g.id, {
            images: [{ base64: cover.base64, mime: cover.mime }],
          });
        } else if (cover.kind === 'saved') {
          await api.setGameCovers(g.id, { reuseSaved: true });
        } else if (cover.kind === 'fromGame') {
          await api.setGameCovers(g.id, { reuseFromGameId: cover.gameId });
        }
      } catch {
        // Game exists; host can set covers from Edit. Still navigate.
      }
      return g;
    },
    {
      onSuccess: (g) => {
        qc.invalidateQueries(['games']); // also matches the versioned keys
        qc.invalidateQueries(['venues']);
        qc.invalidateQueries(['me']);
        navigate(`/games/${g.id}`);
      },
    },
  );

  const pickCover = async (file: File | null) => {
    if (!file) return;
    try {
      const { base64, mime } = await readImageAsDataUrl(file);
      setCover({ kind: 'upload', base64, mime, preview: base64 });
      setCoverError(null);
    } catch (err) {
      setCoverError((err as Error).message || t('error.unknown'));
    }
  };

  const coverPreview =
    cover.kind === 'upload'
      ? cover.preview
      : cover.kind === 'saved' || cover.kind === 'fromGame'
        ? cover.url
        : null;
  const usingSavedCover = cover.kind === 'saved' || cover.kind === 'fromGame';

  if (venuesQ.isLoading) {
    return (
      <div className="createPage">
        <div className="skeleton" style={{ width: '60%', height: 24, marginBottom: 24 }} />
        <div className="skeleton" style={{ width: '100%', height: 48, marginBottom: 16, borderRadius: 12 }} />
        <div className="skeleton" style={{ width: '100%', height: 48, marginBottom: 16, borderRadius: 12 }} />
        <div className="skeleton" style={{ width: '100%', height: 120, borderRadius: 12 }} />
      </div>
    );
  }

  const canSubmit = !!venueAddress.trim() && !createMut.isLoading;

  return (
    <form
      className="createForm"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) createMut.mutate();
      }}
    >
      <header className="createHeader">
        <div className="createHeader-icon">
          <Icon name="plus-sign" size={20} />
        </div>
        <div>
          <h1 className="createHeader-title">{t('create.title')}</h1>
          <p className="createHeader-sub">{t('create.subtitle')}</p>
        </div>
      </header>

      {/* Where */}
      <section className="formSection">
        <h2 className="formSection-title">
          <span className="formSection-num">1</span>
          {t('create.section.where')}
        </h2>
        <div className="field">
          <label className="field-label" htmlFor="venue">
            <Icon name="building-01" size={12} className="icon-inline" />
            {t('create.field.savedPlaces')}
          </label>
          <div className="venueSelectWrap">
            <select
              id="venue"
              className="venueSelect"
              value={venueId}
              onChange={(e) => {
                const id = e.target.value;
                setVenueId(id);
                const v = (venues ?? []).find((x) => x.id === id);
                if (v) {
                  setVenueName(v.name);
                  setVenueAddress(v.address);
                }
              }}
              data-analytics-label="create-venue-select"
              aria-label={t('create.field.savedPlaces')}
            >
              <option value="">
                {(venues ?? []).length === 0
                  ? t('create.field.noSavedPlaces')
                  : t('create.field.savedPlacesPlaceholder')}
              </option>
              {(venues ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} — {v.address}
                </option>
              ))}
            </select>
            <span className="venueSelect-chevron" aria-hidden="true">
              <Icon name="arrow-down-01" size={14} />
            </span>
          </div>
          {selectedVenue && (
            <div className="venueSelectedHint">
              <Icon name={selectedVenue.indoor ? 'building-01' : 'maps'} size={12} />
              <span>
                {selectedVenue.indoor ? 'Indoor' : 'Outdoor'} ·{' '}
                {CURRENCY_SYMBOLS[currency]}
                {(selectedVenue.hourlyPrice / 100).toFixed(2)}/hr
              </span>
            </div>
          )}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="venueName">
            <Icon name="building-01" size={12} className="icon-inline" />
            {t('create.field.placeName')}
          </label>
          <input
            id="venueName"
            value={venueName}
            onChange={(e) => {
              setVenueId('');
              setVenueName(e.target.value);
            }}
            placeholder={t('create.field.placeNamePlaceholder')}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="address">
            <Icon name="map-pin" size={12} className="icon-inline" />
            {t('create.field.venueAddress')}
          </label>
          <input
            id="address"
            value={venueAddress}
            onChange={(e) => {
              setVenueId('');
              setVenueAddress(e.target.value);
            }}
            placeholder={t('create.field.venueAddressPlaceholder')}
          />
        </div>

        {/* PlayType — where the game will be played. Independent from
            Venue.indoor because the venue might be an indoor sports complex
            that also hosts a beach tournament outside. */}
        <div className="field">
          <label className="field-label">
            <Icon name="globe" size={12} className="icon-inline" />
            {t('create.field.playType')}
          </label>
          <div className="playTypePicker" role="radiogroup" aria-label={t('create.field.playType')}>
            {PLAY_TYPES.map((pt) => {
              const active = playType === pt;
              const iconName =
                pt === 'INDOOR' ? 'building-01' : pt === 'BEACH' ? 'tennis-ball' : 'globe';
              return (
                <button
                  key={pt}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`playTypePicker-option${active ? ' isActive' : ''}`}
                  onClick={() => setPlayType(pt)}
                  data-analytics-label={`play-type-${pt.toLowerCase()}`}
                >
                  <Icon name={iconName} size={14} />
                  <span>{t(`create.playType.${pt.toLowerCase()}`)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* The cover image is auto-selected from `playType`
            (BEACH / OUTDOOR / INDOOR) using static assets that ship with
            the app. No input or preview is shown — the host doesn't
            choose or see the cover image during creation. */}
      </section>

      {/* When */}
      <section className="formSection">
        <h2 className="formSection-title">
          <span className="formSection-num">2</span>
          {t('create.section.when')}
        </h2>
        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="start">
              <Icon name="calendar-01" size={12} className="icon-inline" />
              {t('create.field.start')}
            </label>
            <input
              id="start"
              type="datetime-local"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
            />
          </div>
          <div className="field field-duration">
            <label className="field-label" htmlFor="duration">
              <Icon name="clock-01" size={12} className="icon-inline" />
              {t('create.field.duration')}
            </label>
            <input
              id="duration"
              type="number"
              min={1}
              max={6}
              value={durationHours === '' ? '' : durationHours}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  setDurationHours('');
                  return;
                }
                const n = Number(v);
                if (!Number.isNaN(n)) setDurationHours(Math.min(6, Math.max(1, n)));
              }}
              onBlur={(e) => {
                const n = Number(e.target.value);
                if (!Number.isFinite(n) || n < 1) setDurationHours(1);
              }}
            />
          </div>
        </div>
      </section>

      {/* Who */}
      <section className="formSection">
        <h2 className="formSection-title">
          <span className="formSection-num">3</span>
          {t('create.section.who')}
        </h2>

        <div className="field">
          <label className="field-label">
            <Icon name="award-01" size={12} className="icon-inline" />
            {t('create.field.skill')}
          </label>
          {/* Skill chip grid replaces the previously-broken tap handler. Each
              chip is its own button; no race condition between hover / focus
              and the click handler. */}
          <div className="skillChipGrid" role="radiogroup" aria-label="Skill level">
            {SKILL_LEVELS.map((s, i) => (
              <button
                type="button"
                key={s}
                role="radio"
                aria-checked={skill === s}
                className={`skillChip ${skill === s ? 'isActive' : ''}`}
                onClick={() => setSkill(s)}
                data-analytics-label={`create-skill-${s}`}
              >
                <span className="skillChip-icon">
                  <Icon name={SKILL_ICONS[s]} size={14} />
                </span>
                <span className="skillChip-num">{i + 1}</span>
              </button>
            ))}
          </div>
          <div className="field-hint" style={{ marginTop: 6 }}>
            {t(`skill.${skill}`)}
          </div>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="spots">
            <Icon name="user-group" size={12} className="icon-inline" />
            {t('create.field.spots')}
          </label>

          <div className="spotsWrap">
            <div className="spotsStepper">
              <button
                type="button"
                className="stepperBtn"
                onClick={() => setSpotsTotal((s) => Math.max(2, s - 1))}
                disabled={unlimitedSpots}
                aria-label="Decrease spots"
              >
                <Icon name="arrow-down-01" size={14} />
              </button>
              <input
                id="spots"
                type="number"
                min={2}
                max={1000}
                value={unlimitedSpots ? '∞' : spotsTotal}
                disabled={unlimitedSpots}
                onChange={(e) =>
                  setSpotsTotal(Math.max(2, Math.min(1000, Number(e.target.value) || 2)))
                }
                className="stepperInput"
              />
              <button
                type="button"
                className="stepperBtn"
                onClick={() => setSpotsTotal((s) => Math.min(1000, s + 1))}
                disabled={unlimitedSpots}
                aria-label="Increase spots"
              >
                <Icon name="arrow-up-01" size={14} />
              </button>
            </div>

            <label className="toggle">
              <input
                type="checkbox"
                checked={unlimitedSpots}
                onChange={(e) => setUnlimitedSpots(e.target.checked)}
              />
              <span className="toggle-track" />
              <span className="toggle-label">{t('create.field.unlimited')}</span>
            </label>
          </div>
          {unlimitedSpots && <div className="field-hint">{t('create.unlimitedHint')}</div>}
        </div>
      </section>

      {/* Cost */}
      <section className="formSection">
        <h2 className="formSection-title">
          <span className="formSection-num">4</span>
          {t('create.section.cost')}
        </h2>

        <div className="field">
          <label className="field-label" htmlFor="currency">
            <Icon name="dollar-01" size={12} className="icon-inline" />
            {t('create.field.currency')}
          </label>
          <select
            id="currency"
            className="costInput-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
            style={{ width: '100%' }}
          >
            {SUPPORTED_CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {CURRENCY_SYMBOLS[c]} {c}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="cost">
            <Icon name="dollar-01" size={12} className="icon-inline" />
            {t('create.field.totalCost')}
          </label>
          <div className="costInput-row">
            <input
              id="cost"
              type="text"
              inputMode="decimal"
              value={finalCostDecimal}
              onChange={(e) => {
                setCostTouched(true);
                // Allow empty and decimal-only; we parse on submit.
                const v = e.target.value.replace(',', '.');
                if (/^\d*\.?\d{0,2}$/.test(v)) setTotalCostDecimal(v);
                else if (v === '') setTotalCostDecimal('');
              }}
              placeholder={suggestedCostDecimal}
            />
            <span className="costInput-currency" style={{ minWidth: 48 }}>
              {CURRENCY_SYMBOLS[currency]}
            </span>
          </div>
          <div className="field-hint">
            {costTouched
              ? t('create.cost.custom')
              : selectedVenue
                ? t('create.cost.auto', { venue: selectedVenue.name, hours: durationHours === '' ? 1 : durationHours })
                : t('create.cost.manual')}
          </div>
        </div>

        <label className="toggle" style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={isClosed}
            onChange={(e) => setIsClosed(e.target.checked)}
          />
          <span className="toggle-track" />
          <span className="toggle-label">{t('create.field.closed')}</span>
        </label>

        <label className="toggle" style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={hidePlayersFromOutsiders}
            onChange={(e) => setHidePlayersFromOutsiders(e.target.checked)}
          />
          <span className="toggle-track" />
          <span className="toggle-label">{t('create.field.hidePlayers')}</span>
        </label>
        <p className="field-hint">{t('game.hidePlayersHint')}</p>

        <div
          className={`paidBadge ${isPaid ? 'paidBadge-paid' : 'paidBadge-free'}`}
          aria-live="polite"
        >
          <Icon name={isPaid ? 'credit-card' : 'gift'} size={14} />
          <span>
            {isPaid
              ? t('create.paidStatus.on')
              : t('create.paidStatus.off')}
          </span>
        </div>

        <div className="costSummary">
          <div className="costRow">
            <span className="costSummary-label">{t('payments.perPlayer')}</span>
            <strong>
              {CURRENCY_SYMBOLS[currency]}
              {perPlayer}
            </strong>
          </div>
          {!unlimitedSpots && (
            <div className="costRow">
              <span className="costSummary-label">{t('create.splitBetween', { n: spotsTotal })}</span>
            </div>
          )}
        </div>
      </section>

      <section className="formSection">
        <h2 className="formSection-title">
          <span className="formSection-num">5</span>
          {t('create.section.covers')}
        </h2>
        <p className="field-hint" style={{ marginTop: 0 }}>
          {t('game.coversHint')}
        </p>
        <div className="createCovers createCovers-single">
          <button
            type="button"
            className="createCovers-slot"
            onClick={() => coverRef.current?.click()}
          >
            {coverPreview ? (
              <img src={coverPreview} alt="" />
            ) : (
              <span>
                <Icon name="plus-sign" size={18} />
                {t('game.coversAdd')}
              </span>
            )}
          </button>
        </div>
        <input
          ref={coverRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => {
            void pickCover(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
        {usingSavedCover && (
          <p className="field-hint createCovers-reuseHint">
            {t('game.coversReuseHint')}
          </p>
        )}
        {coverPreview && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            style={{ marginTop: 8, alignSelf: 'flex-start' }}
            onClick={() => setCover({ kind: 'none' })}
          >
            {t('game.coversClear')}
          </button>
        )}
        {!coverPreview && (
          <div className="createCovers-fallback">
            <img src={coverForPlayType(playType)} alt="" />
            <span>{t('create.field.coverPreviewHint')}</span>
          </div>
        )}
        {coverError && <div className="error">{coverError}</div>}
      </section>

      <section className="formSection">
        <h2 className="formSection-title">
          <span className="formSection-num">6</span>
          {t('create.section.notes')}
        </h2>
        <div className="field">
          <label className="field-label" htmlFor="notes">
            <Icon name="note-01" size={12} className="icon-inline" />
            {t('common.optional')}
          </label>
          <textarea
            id="notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Bring a light/dark shirt for teams"
          />
        </div>
      </section>

      {createMut.isError && (
        <div className="error">
          <Icon name="bell-dot" size={16} />
          <span>{(createMut.error as Error).message}</span>
        </div>
      )}

      <button type="submit" className="btn" disabled={!canSubmit}>
        <Icon name="plus-sign" size={18} />
        {createMut.isLoading ? t('create.cta.creating') : t('create.cta')}
      </button>
    </form>
  );
}
